import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PuzzleControls from './PuzzleControls';
import PuzzleTimer from './PuzzleTimer';
import PuzzleNumberPad from './PuzzleNumberPad';
import { resolveDisplayImageUrl } from '../lib/assets';

const storageKeyForPuzzle = (puzzle) => `prism-progress:${puzzle.instanceId}`;
const historyLimit = 200;
const cellKey = (row, col) => `${row}:${col}`;

const cloneGrid = (grid) => grid.map((row) => row.map((cell) => cell));
const cloneNotes = (notes = {}) =>
  Object.fromEntries(
    Object.entries(notes).map(([key, values]) => [key, [...values]])
  );

const normalizeNotes = (notes = {}) =>
  Object.fromEntries(
    Object.entries(notes)
      .map(([key, values]) => {
        const normalized = Array.from(
          new Set(
            (Array.isArray(values) ? values : [])
              .map((value) => Number(value))
              .filter((value) => Number.isInteger(value) && value >= 1 && value <= 9)
          )
        ).sort((left, right) => left - right);

        return [key, normalized];
      })
      .filter(([, values]) => values.length)
  );

const loadSavedProgress = (storageKey, adapter, initialState) => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
    return {
      state: saved?.state ? adapter.normalizeState(saved.state) : initialState,
      notes: normalizeNotes(saved?.notes || {}),
      seconds: Number.isFinite(saved?.seconds) ? saved.seconds : 0,
      noteMode: Boolean(saved?.noteMode),
    };
  } catch (error) {
    return {
      state: initialState,
      notes: {},
      seconds: 0,
      noteMode: false,
    };
  }
};

export default function PuzzleShell({ puzzle, adapter }) {
  const initialState = useMemo(
    () => adapter.normalizeState(puzzle.initialState),
    [adapter, puzzle.initialState]
  );
  const givenMask = useMemo(
    () => adapter.getGivenMask(initialState),
    [adapter, initialState]
  );
  const storageKey = useMemo(() => storageKeyForPuzzle(puzzle), [puzzle]);
  const puzzleIcon = resolveDisplayImageUrl(puzzle.puzzleType?.iconUrl);
  const inputSymbols = useMemo(
    () =>
      (puzzle.rules?.symbols || []).map((symbol) => {
        const numericValue = Number(symbol);

        return {
          key: String(symbol),
          label: String(symbol),
          value: Number.isNaN(numericValue) ? symbol : numericValue,
        };
      }),
    [puzzle.rules?.symbols]
  );

  const [state, setState] = useState(() =>
    loadSavedProgress(storageKey, adapter, initialState).state
  );
  const [notes, setNotes] = useState(() =>
    loadSavedProgress(storageKey, adapter, initialState).notes
  );
  const [seconds, setSeconds] = useState(() =>
    loadSavedProgress(storageKey, adapter, initialState).seconds
  );
  const [noteMode, setNoteMode] = useState(() =>
    loadSavedProgress(storageKey, adapter, initialState).noteMode
  );
  const [statusMessage, setStatusMessage] = useState(
    'Your progress is saved locally in this browser while you play.'
  );
  const [tone, setTone] = useState('neutral');
  const [errorCells, setErrorCells] = useState([]);
  const [hintCells, setHintCells] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [history, setHistory] = useState({ past: [], future: [] });

  const selectedCellMeta = useMemo(() => {
    if (!selectedCell) {
      return null;
    }

    const value = state?.[selectedCell.row]?.[selectedCell.col] ?? null;
    const locked = Boolean(givenMask?.[selectedCell.row]?.[selectedCell.col]);

    return {
      ...selectedCell,
      value,
      locked,
      label: `row ${selectedCell.row + 1}, column ${selectedCell.col + 1}`,
      notes: notes[cellKey(selectedCell.row, selectedCell.col)] || [],
    };
  }, [givenMask, notes, selectedCell, state]);

  const symbolCounts = useMemo(
    () =>
      state.flat().reduce((counts, cell) => {
        if (cell !== null && cell !== undefined) {
          const key = String(cell);
          counts[key] = (counts[key] || 0) + 1;
        }

        return counts;
      }, {}),
    [state]
  );

  useEffect(() => {
    const saved = loadSavedProgress(storageKey, adapter, initialState);
    setState(saved.state);
    setNotes(saved.notes);
    setSeconds(saved.seconds);
    setNoteMode(saved.noteMode);
    setErrorCells([]);
    setHintCells([]);
    setStatusMessage('Your progress is saved locally in this browser while you play.');
    setTone('neutral');
    setSelectedCell(null);
    setHistory({ past: [], future: [] });
  }, [adapter, initialState, storageKey]);

  useEffect(() => {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        state,
        notes,
        seconds,
        noteMode,
      })
    );
  }, [noteMode, notes, seconds, state, storageKey]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setSeconds((currentSeconds) => currentSeconds + 1);
    }, 1000);

    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const targetTag = event.target?.tagName?.toLowerCase();
      if (targetTag === 'input' || targetTag === 'textarea' || targetTag === 'select') {
        return;
      }

      if (/^[1-9]$/.test(event.key)) {
        event.preventDefault();
        handlePadInput(Number(event.key));
        return;
      }

      if (event.key === 'Backspace' || event.key === 'Delete' || event.key === '0') {
        event.preventDefault();
        handleClearSelectedCell();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const pushHistorySnapshot = () => ({
    state: cloneGrid(state),
    notes: cloneNotes(notes),
  });

  const commitBoardUpdate = (nextState, nextNotes, message, nextTone = 'neutral') => {
    setHistory((currentHistory) => ({
      past: [...currentHistory.past.slice(-(historyLimit - 1)), pushHistorySnapshot()],
      future: [],
    }));
    setState(nextState);
    setNotes(normalizeNotes(nextNotes));
    setErrorCells([]);
    setHintCells([]);
    setStatusMessage(message);
    setTone(nextTone);
  };

  const handleCellChange = (row, col, value) => {
    const nextState = state.map((currentRow, rowIndex) =>
      rowIndex === row
        ? currentRow.map((cell, colIndex) => (colIndex === col ? value : cell))
        : currentRow
    );
    const nextNotes = cloneNotes(notes);
    delete nextNotes[cellKey(row, col)];
    commitBoardUpdate(nextState, nextNotes, 'Progress saved locally.');
  };

  const handleCellSelect = (row, col) => {
    setSelectedCell({ row, col });
    setStatusMessage(
      givenMask[row][col]
        ? `Row ${row + 1}, column ${col + 1} is part of the starting grid.`
        : `Selected row ${row + 1}, column ${col + 1}.`
    );
    setTone(givenMask[row][col] ? 'warning' : 'neutral');
  };

  const handlePadInput = (value) => {
    if (!selectedCellMeta) {
      setStatusMessage('Select a cell first, then choose a number.');
      setTone('warning');
      return;
    }

    if (selectedCellMeta.locked) {
      setStatusMessage('That cell is part of the starting grid and cannot be changed.');
      setTone('warning');
      return;
    }

    if (noteMode) {
      if (selectedCellMeta.value !== null) {
        setStatusMessage('Clear the current value before adding notes to this cell.');
        setTone('warning');
        return;
      }

      const key = cellKey(selectedCellMeta.row, selectedCellMeta.col);
      const nextNotes = cloneNotes(notes);
      const currentNotes = nextNotes[key] ? [...nextNotes[key]] : [];

      if (currentNotes.includes(value)) {
        nextNotes[key] = currentNotes.filter((note) => note !== value);
      } else {
        nextNotes[key] = [...currentNotes, value].sort((left, right) => left - right);
      }

      if (!nextNotes[key].length) {
        delete nextNotes[key];
      }

      commitBoardUpdate(
        cloneGrid(state),
        nextNotes,
        `Updated notes for row ${selectedCellMeta.row + 1}, column ${selectedCellMeta.col + 1}.`
      );
      return;
    }

    handleCellChange(selectedCellMeta.row, selectedCellMeta.col, value);
  };

  const handleClearSelectedCell = () => {
    if (!selectedCellMeta) {
      setStatusMessage('Select a cell before clearing it.');
      setTone('warning');
      return;
    }

    if (selectedCellMeta.locked) {
      setStatusMessage('Starting clues cannot be cleared.');
      setTone('warning');
      return;
    }

    const key = cellKey(selectedCellMeta.row, selectedCellMeta.col);

    if (selectedCellMeta.value !== null) {
      handleCellChange(selectedCellMeta.row, selectedCellMeta.col, null);
      return;
    }

    if (selectedCellMeta.notes.length) {
      const nextNotes = cloneNotes(notes);
      delete nextNotes[key];
      commitBoardUpdate(
        cloneGrid(state),
        nextNotes,
        `Cleared notes for row ${selectedCellMeta.row + 1}, column ${selectedCellMeta.col + 1}.`
      );
      return;
    }

    setStatusMessage('That cell is already empty.');
    setTone('neutral');
  };

  const handleToggleNoteMode = () => {
    setNoteMode((current) => !current);
    setStatusMessage(
      noteMode
        ? 'Note mode turned off. Number pad will place digits directly.'
        : 'Note mode turned on. Number pad will add or remove pencil marks.'
    );
    setTone(noteMode ? 'neutral' : 'warning');
  };

  const handleUndo = () => {
    if (!history.past.length) {
      setStatusMessage('Nothing to undo yet.');
      setTone('neutral');
      return;
    }

    const previousSnapshot = history.past[history.past.length - 1];

    setHistory({
      past: history.past.slice(0, -1),
      future: [pushHistorySnapshot(), ...history.future].slice(0, historyLimit),
    });
    setState(cloneGrid(previousSnapshot.state));
    setNotes(cloneNotes(previousSnapshot.notes));
    setErrorCells([]);
    setHintCells([]);
    setStatusMessage('Undid the previous move.');
    setTone('neutral');
  };

  const handleRedo = () => {
    if (!history.future.length) {
      setStatusMessage('Nothing to redo right now.');
      setTone('neutral');
      return;
    }

    const [nextSnapshot, ...remainingFuture] = history.future;

    setHistory({
      past: [...history.past.slice(-(historyLimit - 1)), pushHistorySnapshot()],
      future: remainingFuture,
    });
    setState(cloneGrid(nextSnapshot.state));
    setNotes(cloneNotes(nextSnapshot.notes));
    setErrorCells([]);
    setHintCells([]);
    setStatusMessage('Redid the move.');
    setTone('neutral');
  };

  const handleReset = () => {
    setState(initialState);
    setNotes({});
    setSeconds(0);
    setErrorCells([]);
    setHintCells([]);
    setSelectedCell(null);
    setHistory({
      past: [...history.past.slice(-(historyLimit - 1)), pushHistorySnapshot()],
      future: [],
    });
    setStatusMessage('Puzzle reset to the starting grid.');
    setTone('warning');
  };

  const handleCheck = () => {
    const result = adapter.check(state);
    setErrorCells(result.conflicts || []);
    setHintCells([]);
    setStatusMessage(result.message);
    setTone(result.valid ? (result.complete ? 'success' : 'neutral') : 'error');

    if (result.conflicts?.length) {
      setSelectedCell(result.conflicts[0]);
    }
  };

  const handleHint = () => {
    const hint = adapter.getHint(state);
    setErrorCells(hint.highlights?.invalidCells || []);
    setHintCells(hint.highlights?.targetCells || []);
    setStatusMessage(hint.explanation || hint.message);
    setTone(hint.status === 'ok' ? 'warning' : hint.status === 'invalid' ? 'error' : 'neutral');

    if (hint.highlights?.targetCells?.length) {
      setSelectedCell(hint.highlights.targetCells[0]);
    }

    if (hint.highlights?.invalidCells?.length) {
      setSelectedCell(hint.highlights.invalidCells[0]);
    }
  };

  const utilityButtonClass =
    'rounded-full border px-3 py-2 text-xs font-semibold transition sm:text-sm';

  return (
    <section className="space-y-6">
      <header className="rounded-[1.75rem] border border-white/70 bg-white/85 p-6 shadow-xl shadow-slate-200/60 sm:p-7">
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
          <Link to="/catalog" className="transition hover:text-brand-cyan">
            Catalog
          </Link>
          <span>/</span>
          <Link
            to={`/catalog/${puzzle.puzzleType.slug}`}
            className="transition hover:text-brand-cyan"
          >
            {puzzle.puzzleType.name}
          </Link>
        </div>
        <div className="mt-4 flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[1.25rem] border border-slate-200 bg-slate-50 p-3 shadow-sm sm:h-20 sm:w-20">
              {puzzleIcon ? (
                <img
                  src={puzzleIcon}
                  alt={`${puzzle.puzzleType.name} icon`}
                  className="h-full w-full object-contain"
                />
              ) : (
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Prism
                </span>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-cyan">
                {puzzle.puzzleType.name}
              </p>
              <h1 className="mt-2 text-3xl font-semibold text-slate-900 md:text-[2.35rem]">
                {puzzle.title}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 md:text-base">
                {puzzle.instructions || puzzle.puzzleType.description}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700">
                  {puzzle.difficulty}
                </span>
                {puzzle.metadata?.puzzleNumber ? (
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700">
                    Puzzle {puzzle.metadata.puzzleNumber}
                  </span>
                ) : null}
                {puzzle.metadata?.bookCode ? (
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700">
                    Book-linked
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
              Autosave on
            </span>
            <PuzzleTimer seconds={seconds} />
          </div>
        </div>
      </header>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_290px]">
        <div className="rounded-[1.75rem] border border-white/70 bg-white/85 p-4 shadow-lg shadow-slate-200/50 sm:p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                Board
              </p>
              <h2 className="mt-1 text-xl font-semibold text-slate-900">
                Solve in the browser
              </h2>
            </div>
            <Link
              to={`/catalog/${puzzle.puzzleType.slug}`}
              className="text-sm font-semibold text-brand-magenta transition hover:text-brand-cyan"
            >
              Back to {puzzle.puzzleType.name}
            </Link>
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleToggleNoteMode}
              className={`${utilityButtonClass} ${
                noteMode
                  ? 'border-brand-cyan bg-brand-cyan text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-brand-cyan hover:text-brand-cyan'
              }`}
            >
              {noteMode ? 'Notes on' : 'Notes off'}
            </button>
            <button
              type="button"
              onClick={handleUndo}
              disabled={!history.past.length}
              className={`${utilityButtonClass} border-slate-200 bg-white text-slate-700 hover:border-brand-cyan hover:text-brand-cyan disabled:cursor-not-allowed disabled:opacity-50`}
            >
              Undo
            </button>
            <button
              type="button"
              onClick={handleRedo}
              disabled={!history.future.length}
              className={`${utilityButtonClass} border-slate-200 bg-white text-slate-700 hover:border-brand-cyan hover:text-brand-cyan disabled:cursor-not-allowed disabled:opacity-50`}
            >
              Redo
            </button>
            <p className="hidden text-xs text-slate-500 xl:ml-auto xl:block">
              Keyboard entry still works if you prefer it.
            </p>
          </div>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_9.75rem] xl:grid-cols-[minmax(0,1fr)_10.5rem]">
            <div className="overflow-x-auto">
              <div className="flex min-w-fit justify-center">
                {adapter.renderBoard({
                  state,
                  givenMask,
                  onCellSelect: handleCellSelect,
                  errorCells,
                  hintCells,
                  selectedCell,
                  selectedValue: selectedCellMeta?.value ?? null,
                  notes,
                })}
              </div>
            </div>
            {inputSymbols.length ? (
              <PuzzleNumberPad
                symbols={inputSymbols}
                counts={symbolCounts}
                selectedCellLabel={selectedCellMeta?.label || ''}
                selectedValue={selectedCellMeta?.value ?? null}
                selectedHasNotes={Boolean(selectedCellMeta?.notes.length)}
                selectionLocked={Boolean(selectedCellMeta?.locked)}
                onSelectSymbol={handlePadInput}
                onClear={handleClearSelectedCell}
              />
            ) : null}
          </div>
        </div>
        <aside className="space-y-5 rounded-[1.75rem] border border-white/70 bg-white/90 p-5 shadow-lg shadow-slate-200/50">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-400">
              Puzzle tools
            </p>
            <h2 className="mt-2 text-xl font-semibold text-slate-900">
              Check, hint, and reset
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Use checks when you want reassurance, or ask for a gentle nudge without giving away the full puzzle.
            </p>
          </div>
          <PuzzleControls
            onCheck={handleCheck}
            onHint={handleHint}
            onReset={handleReset}
            statusMessage={statusMessage}
            tone={tone}
            hintDisabled={!puzzle.capabilities?.hint}
          />
          <div className="space-y-3 rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
            <div>
              <p className="font-semibold text-slate-800">How this version works</p>
              <p className="mt-1">
                Progress stays on this device for now, so you can leave and come back later in the same browser.
              </p>
            </div>
            <div>
              <p className="font-semibold text-slate-800">Current Sudoku rules</p>
              <p className="mt-1">
                Fill every row, column, and 3x3 box with the digits 1 through 9 without repeats.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
